import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { nameValidation, statusValidation } from "validationSchemas/common";
import { UIInput, UIReactSelect } from "components/Inputfields";

// ---------- Types ----------
export type AttributeFormValues = {
  name: string;
  parameters: string; // comma-separated
  description: string;
  statuss: string; // "Active" | "Inactive"
};

// ---------- Constants ----------
const INPUT_BG = "#fafafa";
const BORDER = "#e5e7eb";

// ---------- Reusable UI component (same file) ----------
export function AttributeFormCard({
  title,
  formik,
  formSubmitting,
  onCancel,
}: {
  title: string;
  formik: ReturnType<typeof useFormik<AttributeFormValues>>;
  formSubmitting: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="content container">
      <div className="row">
        <div className="col-md-12 d-flex justify-content-center">
          <div className="col-md-9">
            <div className="mb-5">
              <div className="mt-1 mb-3">
                <h3>{title}</h3>
              </div>
            </div>

            {/* card */}
            <div className="box-shadow shadow-border-3 mb-5">
              {/* header */}
              <div
                className="d-flex align-items-center ms-4"
                style={{ padding: "33px 18px", borderBottom: "1px solid #eef2f7" }}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{ width: 26, height: 26 }}
                >
                  <i
                    className="bi bi-credit-card-2-front"
                    style={{ fontSize: 26, color: "#155dfc", lineHeight: 1 }}
                  />
                </span>
                <div className="fs-19 roboto-new ms-2">Attribute Details</div>
              </div>

              {/* body */}
              <form onSubmit={formik.handleSubmit} className="mx-4 mb-4">
                <div style={{ padding: "54px 18px 0px 18px" }}>
                  <div className="ms-1 mb-2 fs-19 roboto-new">Attribute Information</div>
                  <hr />

                  <div className="row ms-1" id="row-overlap">
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <UIInput
                        formik={formik}
                        name="name"
                        label="Name"
                        required
                        placeholder="Enter attribute name"
                      />
                    </div>
                    <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                      <UIInput
                        formik={formik}
                        name="parameters"
                        label="Value"
                        required
                        placeholder="Enter values separated by comma"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="col-lg-12 roboto-new" id="row-overlap">
                    <label className="form-label">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control"
                      rows={4}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{
                        borderRadius: 10,
                        background: INPUT_BG,
                        borderColor: BORDER,
                        fontSize: 14,
                      }}
                      placeholder="Enter description (optional)"
                    />
                  </div>

                  {/* Status */}
                  <div className="col-12 ms-lg-3" id="row-overlap">
                    <div className="row align-items-center mt-4">
                      <div className="col-lg-3 roboto-new mb-3" id="row-overlap">
                        <UIReactSelect
                          formik={formik}
                          name="statuss"
                          label="Status"
                          options={[
                            { value: "Active", label: "Active" },
                            { value: "Inactive", label: "Inactive" },
                          ]}
                          placeholder="Select"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 mb-2" id="row-overlap">
                  <hr className="ms-4 me-3" />
                </div>

                {/* footer */}
                <div
                  className="d-flex justify-content-between align-items-center ms-1"
                  style={{ padding: "14px 18px" }}
                >
                  <button
                    type="button"
                    onClick={onCancel}
                    className="btn back-btn surface-btn fw-bold fs-16"
                  >
                    <i className="bi bi-chevron-left me-2" />
                    Back
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                    disabled={formSubmitting}
                  >
                    {formSubmitting ? "Submitting..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
            {/* /card */}
          </div>
        </div>
      </div>
    </div>
  );
}


